import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Field, DataTable } from "@/pages/FridgeLogs";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { UserPlus, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "staff", label: "Staff" },
];

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role !== "owner") {
      toast.error("Only owners can manage the team");
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the team?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const roleCounts = users.reduce((a, u) => ((a[u.role] = (a[u.role] || 0) + 1), a), {});

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="team-page">
      <PageHeader
        eyebrow="Owner control"
        title="Team & access"
        description="Manage who can log in to your operations console — and the level of access they get."
        actions={
          <Button data-testid="team-invite-button" onClick={() => setOpen(true)} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
            <UserPlus className="w-4 h-4 mr-2" /> Add team member
          </Button>
        }
      />

      <div className="grid grid-cols-3 border border-[#e5e2d9] bg-white mb-6">
        {ROLES.map((r, i) => (
          <div key={r.value} className={`p-5 ${i < 2 ? "border-r border-[#e5e2d9]" : ""}`} data-testid={`team-stat-${r.value}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">{r.label}s</div>
            <div className="font-display text-3xl mt-2">{roleCounts[r.value] || 0}</div>
          </div>
        ))}
      </div>

      <DataTable
        empty="No users yet."
        head={["Name", "Email", "Role", "Joined", ""]}
        rows={users}
        testid="team-table"
        render={(u, idx) => {
          const isSelf = u.id === user?.id;
          return (
            <tr key={u.id} data-testid={`team-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40">
              <td className="px-4 py-3 text-sm">
                <div className="font-medium">{u.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-[#4e7b5b]">you</span>}</div>
              </td>
              <td className="px-4 py-3 text-sm text-[#57534e]">{u.email}</td>
              <td className="px-4 py-3">
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                  <SelectTrigger data-testid={`team-role-${idx}`} className="h-8 w-[120px] rounded-full px-3 text-xs border-0 bg-transparent">
                    <StatusPill tone={u.role === "owner" ? "brand" : u.role === "manager" ? "warning" : "neutral"}>{u.role}</StatusPill>
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3 text-sm whitespace-nowrap text-[#8a8476]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-3 text-right">
                {!isSelf && (
                  <button data-testid={`team-delete-${idx}`} onClick={() => del(u)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          );
        }}
      />

      <div className="mt-6 flex items-start gap-3 text-xs text-[#57534e] bg-[#fdf3e1] border border-[#d9953d]/30 px-4 py-3 rounded-[3px]">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-[#d9953d] shrink-0" />
        <div>
          <div className="font-medium text-[#1c1917] mb-0.5">Heads-up</div>
          Self sign-up at <span className="font-mono">/signup</span> still creates new accounts as <span className="font-mono">staff</span>. Disable that route or invite-only on the roadmap.
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add team member</DialogTitle></DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="team-form">
            <Field label="Full name"><Input required data-testid="team-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <Field label="Email"><Input type="email" required data-testid="team-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <Field label="Temporary password">
              <Input type="text" required minLength={6} data-testid="team-password-input" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="bg-white rounded-[4px] font-mono" placeholder="Share securely with the new user" />
            </Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="team-role-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="team-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Adding…" : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
